import { useEffect, useState } from 'react';
import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppShell,
  Avatar,
  MobileShell,
  Sidebar,
  Icon,
  useAppAccent,
  useTweaks,
  useViewport,
  type MobileTab,
  type MobileTabRenderProps,
  type SidebarItemRenderProps,
} from '@k-os/ui';
import {
  NAV_GROUPS,
  HEALTH_NAV_GROUPS,
  FINANCE_NAV_GROUPS,
  type NavItem,
  type NavGroup,
} from '@k-os/core';
import { QuickCapture } from '../components/QuickCapture';
import { SearchPalette } from '../components/SearchPalette';
import { TaskPreviewDrawer } from '../components/TaskPreviewDrawer';
import { UserMenu } from '../components/UserMenu';
import { AppIcon } from '../components/AppIcon';
import { AppSwitcher } from '../components/AppSwitcher';
import { TweaksProvider } from '../lib/tweaks-context';
import { logout, type AuthUser, type AuthWorkspace } from '../api/auth';
import { useSession } from '../api/auth-hooks';
import { useCounts } from '../api/tasks';
import { useFinanceReviewCounts } from '../api/finance';
import { useActiveWorkouts } from '../api/health';
import { useAccentPrefs, usePlatformPref, usePatchPreference } from '../api/preferences';
import { usePreviewStore } from '../lib/preview-store';
import { usePomodoroStore, usePomodoroClock } from '../lib/pomodoro-store';
import { usePomodoroFinish } from '../lib/use-pomodoro-finish';
import overlayStyles from './pomodoro-overlay.module.css';
import { ApiError } from '../api/client';
import kLogoUrl from '../assets/k-os-k-logo-transparent.svg';

/**
 * Synthetic nav item id for the dynamic "Active workout" sidebar entry.
 * Not part of HEALTH_NAV_GROUPS (packages/core stays static) — spliced in
 * at render time by healthNavGroupsWithActiveWorkout below, the same way
 * Tasks' counts/alerts decorate static items but one level up (a whole
 * item instead of a badge).
 */
const ACTIVE_WORKOUT_NAV_ID = 'health-active-workout';
const POMODORO_NAV_ID = 'pomodoro';

export const Route = createRootRoute({
  component: RootLayout,
});

const TITLES: Record<string, string> = {
  today: 'Today',
  inbox: 'Inbox',
  calendar: 'Calendar',
  upcoming: 'Upcoming',
  waiting: 'Waiting · Follow-ups',
  all: 'All tasks',
  projects: 'Projects',
  areas: 'Areas',
  people: 'People',
  review: 'Review',
  pomodoro: 'Pomodoro Timer',
  health: 'K-OS Health',
  apps: 'Applications',
  'finance-dashboard': 'Dashboard',
  'finance-review': 'Review',
  'finance-transactions': 'Transactions',
  'finance-import': 'Import',
  'finance-transfers': 'Transfers',
  'finance-accounts': 'Accounts',
  'finance-categories': 'Categories',
  'finance-rules': 'Rules',
  'finance-budgets': 'Budgets',
  'finance-reports': 'Reports',
  'finance-settings': 'Settings',
};

type AppHomePath = '/tasks' | '/health' | '/finance' | '/apps';

// Tasks-app bottom bar. Center "Capture" stays here because Quick Capture
// is a Tasks-app concept.
const TASKS_MOBILE_TABS: MobileTab[] = [
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'inbox', label: 'Inbox', icon: 'inbox' },
  { id: 'capture', label: 'Capture', icon: 'plus', capture: true },
  { id: 'people', label: 'People', icon: 'people' },
  { id: 'more', label: 'More', icon: 'moreH' },
];

// Health-app bottom bar. No quick-capture center button — Health doesn't
// have a global capture flow yet and we suppress the Tasks one inside the
// Health area on desktop too (see the sidebar topSlot gate).
const HEALTH_MOBILE_TABS: MobileTab[] = [
  { id: 'health-today', label: 'Today', icon: 'today' },
  { id: 'health-workouts', label: 'Workouts', icon: 'upcoming' },
  { id: 'health-checkin', label: 'Check-in', icon: 'inboxAlt' },
  { id: 'health-measurements', label: 'Stats', icon: 'list' },
  { id: 'health-more', label: 'More', icon: 'moreH' },
];

const TASKS_MOBILE_TAB_PATHS: Record<string, string> = {
  today: '/tasks',
  inbox: '/tasks/inbox',
  people: '/tasks/people',
  more: '/tasks/review',
};

const HEALTH_MOBILE_TAB_PATHS: Record<string, string> = {
  'health-today': '/health',
  'health-workouts': '/health/workouts',
  'health-checkin': '/health/checkin',
  'health-measurements': '/health/measurements',
  'health-more': '/health/reports',
};

const PUBLIC_PATHS = new Set(['/login', '/signup']);

/**
 * Builds the Sidebar's `renderItem` callback. Almost always a plain
 * `<Link to={item.path}>`, except for the synthetic "Active workout" item,
 * which needs a `search` param the static NavItem shape doesn't carry
 * (one active session → jump straight to its tracker; several → open the
 * chooser). Threading `activeWorkoutSessions` in here keeps that one bit
 * of routing knowledge next to the rest of the render wiring instead of
 * teaching NavItem about search params.
 */
function makeRenderItem(activeWorkoutSessions: readonly { id: string }[]) {
  return function renderItem(item: NavItem, props: SidebarItemRenderProps) {
    if (item.id === ACTIVE_WORKOUT_NAV_ID) {
      const search =
        activeWorkoutSessions.length === 1
          ? { tab: 'today' as const, session: activeWorkoutSessions[0]!.id }
          : { tab: 'today' as const, choose: true as const };
      return (
        <Link to="/health/workouts" search={search} className={props.className}>
          {props.children}
        </Link>
      );
    }
    if (item.id === POMODORO_NAV_ID) {
      return <PomodoroNavLink item={item} props={props} />;
    }
    return (
      <Link to={item.path} className={props.className}>
        {props.children}
      </Link>
    );
  };
}

/**
 * The sidebar's Pomodoro item. While a timer is running or paused the pill
 * takes that mode's colour (focus red / short-break green / long-break blue).
 */
function PomodoroNavLink({ item, props }: { item: NavItem; props: SidebarItemRenderProps }) {
  const status = usePomodoroStore((s) => s.status);
  const mode = usePomodoroStore((s) => s.mode);
  const active = status === 'idle' ? '' : overlayStyles[`navMode_${mode}`];
  return (
    <Link to={item.path} className={`${props.className} ${overlayStyles.navPomodoro} ${active}`}>
      {props.children}
      <PomodoroNavClock />
    </Link>
  );
}

/**
 * Live countdown in the sidebar's Pomodoro item while the timer is running or
 * paused. A leaf component on purpose: the 250ms tick re-renders only this
 * span, not the whole root layout.
 */
function PomodoroNavClock() {
  const status = usePomodoroStore((s) => s.status);
  const { label } = usePomodoroClock();
  if (status === 'idle') return null;
  return <span className={overlayStyles.navClock}>{label}</span>;
}

/** Mounts the finish watcher (chime, notification, tab title) once. */
function PomodoroFinishWatcher() {
  usePomodoroFinish();
  return null;
}

function renderTabFor(pathMap: Record<string, string>) {
  return function renderTab(tab: MobileTab, props: MobileTabRenderProps) {
    const path = pathMap[tab.id] ?? '/';
    return (
      <Link to={path} className={props.className}>
        {props.children}
      </Link>
    );
  };
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function buildUserMeta(user: AuthUser, workspace: AuthWorkspace | null): {
  name: string;
  initials: string;
  meta: string;
} {
  return {
    name: user.displayName,
    initials: deriveInitials(user.displayName),
    meta: workspace?.name ?? user.email,
  };
}

function RootLayout() {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [qcOpen, setQcOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';

  // Aurora per-app accent (blocks A2 + A4): resolve the accent for the current
  // app and stamp `--accent*` on <html>. Recomputes on app + theme change and
  // trips the finance-charts observer. Prefs come from the per-user server map
  // (`useAccentPrefs`), with localStorage as the first-paint cache so there's
  // no flash before GET resolves.
  const accentPrefs = useAccentPrefs();
  useAppAccent(getCurrentAppSlug(location.pathname), accentPrefs);

  // Theme / density persistence: the `platform`-scope prefs feed `useTweaks`
  // (server = source of truth, localStorage = instant cache), and each change
  // write-throughs via PATCH. `useTweaks` is called ONCE here (up with the
  // other hooks, before any early return, for hook-order safety) and published
  // via <TweaksProvider> so the Settings → Appearance page can drive it — the
  // topbar popover that used to own it is gone.
  const platformPref = usePlatformPref();
  const patchPreference = usePatchPreference();
  const tweaksSync = {
    serverPrefs: platformPref
      ? { theme: platformPref.theme, density: platformPref.density }
      : null,
    onPersist: (patch: { theme?: string; density?: string }) =>
      patchPreference.mutate({ scope: 'platform' as const, ...patch }),
  };
  const [tweaks, setTweak] = useTweaks(tweaksSync);

  const previewTaskId = usePreviewStore((s) => s.taskId);
  // `reset` closes unconditionally: a real route change has already happened
  // (and, if the description was dirty, was confirmed via the drawer's blocker),
  // so there's nothing left to guard here.
  const resetPreview = usePreviewStore((s) => s.reset);

  const isPublic = PUBLIC_PATHS.has(location.pathname);

  const sessionQuery = useSession();
  const countsQuery = useCounts();
  const counts = countsQuery.data;

  // Finance "Review" badge — only fetched while inside the Finance app so we
  // don't poll the endpoint from Tasks/Health. Sum of the three triage counts.
  const inFinance = location.pathname.startsWith('/finance');
  const financeReviewCounts = useFinanceReviewCounts({
    enabled: !!sessionQuery.data && inFinance,
  });
  const financeReviewTotal = financeReviewCounts.data
    ? financeReviewCounts.data.suggestedTransfers +
      financeReviewCounts.data.pendingSuggestions +
      financeReviewCounts.data.uncategorized
    : undefined;

  // Active-workout sidebar entry — only polled while inside Health, same
  // gating as the Finance review badge above.
  const inHealth = location.pathname.startsWith('/health');
  const activeWorkoutsQuery = useActiveWorkouts({
    enabled: !!sessionQuery.data && inHealth,
  });
  const activeWorkoutCount = activeWorkoutsQuery.data?.workouts.length ?? 0;
  const activeWorkoutSessions = activeWorkoutsQuery.data?.workouts ?? [];

  // Map sidebar nav ids → counts + alerts from the API.
  const navCounts: Record<string, number | undefined> = {
    today: counts?.today,
    inbox: counts?.inbox,
    upcoming: counts?.upcoming,
    waiting: counts?.waiting,
    all: counts?.all,
    review: counts?.review,
    projects: counts?.projects,
    areas: counts?.areas,
    people: counts?.people,
    'finance-review': financeReviewTotal,
  };
  const navAlerts: Record<string, string | undefined> = {
    today: counts && counts.todayOverdue > 0 ? String(counts.todayOverdue) : undefined,
    waiting: counts && counts.waitingStale > 0 ? String(counts.waitingStale) : undefined,
    [ACTIVE_WORKOUT_NAV_ID]: activeWorkoutCount > 0 ? String(activeWorkoutCount) : undefined,
  };
  // Inbox is the only nav item we visually disable when empty — the rest are
  // pages the user might still want to open (e.g. People with zero contacts).
  const disabledNavIds = counts && counts.inbox === 0 ? ['inbox'] : [];

  // Redirect handling.
  useEffect(() => {
    if (sessionQuery.isLoading) return;
    const is401 =
      sessionQuery.error instanceof ApiError && sessionQuery.error.status === 401;

    if (is401 && !isPublic) {
      void navigate({ to: '/login' });
    } else if (sessionQuery.data && isPublic) {
      // Authenticated user landing on /login or /signup goes to the app
      // picker, not directly into Tasks. K-OS is multi-app now.
      void navigate({ to: '/apps' });
    }
  }, [sessionQuery.isLoading, sessionQuery.error, sessionQuery.data, isPublic, navigate]);

  // ⌘K / Ctrl-K → Quick Capture; ⌘/ / Ctrl-/ → Search. Both only meaningful
  // when authenticated. Opening one closes the other so only a single overlay
  // is ever mounted. `Ctrl+/` can arrive as key `'/'` on some layouts.
  useEffect(() => {
    if (!sessionQuery.data) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(false);
        setQcOpen(true);
      } else if (e.key === '/') {
        e.preventDefault();
        setQcOpen(false);
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sessionQuery.data]);

  // Preview is transient — close it whenever the route changes.
  useEffect(() => {
    resetPreview();
  }, [location.pathname, resetPreview]);

  // Mobile has no side drawer (D8): send the user to the detail page instead
  // when a preview is requested, then clear the transient preview state.
  useEffect(() => {
    if (isMobile && previewTaskId) {
      const id = previewTaskId;
      resetPreview();
      void navigate({ to: '/tasks/item/$id', params: { id } });
    }
  }, [isMobile, previewTaskId, navigate, resetPreview]);

  async function handleLogout() {
    try {
      await logout();
    } catch {
      /* fall through — we clear locally anyway */
    }
    qc.removeQueries({ queryKey: ['session'] });
    qc.clear();
    void navigate({ to: '/login' });
  }

  // Public pages (login / signup) render bare, no shell, no gating.
  if (isPublic) {
    return <Outlet />;
  }

  // While we don't know yet whether the user is authenticated, render nothing.
  if (sessionQuery.isLoading || (!sessionQuery.data && !sessionQuery.error)) {
    return <div style={{ minHeight: '100vh', background: 'var(--canvas)' }} />;
  }
  if (!sessionQuery.data) {
    return <div style={{ minHeight: '100vh', background: 'var(--canvas)' }} />;
  }

  const { user, workspace } = sessionQuery.data;
  const userMeta = buildUserMeta(user, workspace);

  const activeId = getActiveNavId(location.pathname);
  const currentApp = getCurrentAppBrand(location.pathname);
  const slug = getCurrentAppSlug(location.pathname);

  const title = TITLES[activeId] ?? 'K-OS';

  // The Pomodoro page is a full-viewport layer, not a page inside the shell.
  // The shell isn't mounted underneath (the layer is opaque), which keeps the
  // route's <Outlet/> rendered exactly once. The preview drawer overlays the
  // right edge instead of being a third AppShell column.
  if (location.pathname === '/tasks/pomodoro' || location.pathname === '/tasks/pomodoro/') {
    return (
      <TweaksProvider value={{ tweaks, setTweak }}>
        <div className={overlayStyles.overlay}>
          <Outlet />
        </div>
        {previewTaskId && !isMobile && (
          <aside className={overlayStyles.drawer}>
            <TaskPreviewDrawer taskId={previewTaskId} />
          </aside>
        )}
        <QuickCapture open={qcOpen} onClose={() => setQcOpen(false)} />
        <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        <PomodoroFinishWatcher />
      </TweaksProvider>
    );
  }

  if (isMobile) {
    // Pick the tab set + path map that matches the app the user is in. The
    // ids align with the sidebar nav ids (see HEALTH_NAV_GROUPS) so the
    // active-state highlighting carries over between mobile and desktop
    // without any extra mapping.
    const tabs = inHealth ? HEALTH_MOBILE_TABS : TASKS_MOBILE_TABS;
    const pathMap = inHealth ? HEALTH_MOBILE_TAB_PATHS : TASKS_MOBILE_TAB_PATHS;
    return (
      <TweaksProvider value={{ tweaks, setTweak }}>
        <MobileShell
          title={title}
          topbarActions={
            <>
              <AppSwitcher currentSlug={slug} />
              <UserMenu
                user={{ displayName: userMeta.name, platformRole: user.platformRole ?? null }}
                onSignOut={handleLogout}
                trigger={
                  <button
                    type="button"
                    className="kos-icon-btn"
                    aria-label="Open user menu"
                    style={{ padding: '4px' }}
                  >
                    <Avatar person={{ initials: userMeta.initials, color: null }} size={22} />
                  </button>
                }
              />
            </>
          }
          tabs={tabs}
          activeId={activeId}
          onCapture={() => setQcOpen(true)}
          renderTab={renderTabFor(pathMap)}
        >
          <Outlet />
        </MobileShell>
        <QuickCapture open={qcOpen} onClose={() => setQcOpen(false)} />
        <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        <PomodoroFinishWatcher />
      </TweaksProvider>
    );
  }

  // Desktop sidebar topSlot: Quick add + Search. Search now renders for ALL
  // apps (it replaces the removed topbar Search everywhere). Quick add stays
  // suppressed inside Health/Finance — those apps have their own per-screen
  // primary actions (start workout, log check-in, etc.) and no global Tasks-
  // style capture flow yet, so surfacing it there would imply one that doesn't
  // exist.
  const inHealthApp = location.pathname.startsWith('/health');
  const inFinanceApp = location.pathname.startsWith('/finance');
  const showQuickAdd = !inHealthApp && !inFinanceApp;
  const sidebarTop = (
    <>
      {showQuickAdd && (
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            marginBottom: 6,
          }}
          onClick={() => setQcOpen(true)}
        >
          <Icon name="plus" size={12} /> Quick add
          <span
            className="kos-kbd"
            style={{
              marginLeft: 'auto',
              color: 'rgba(245,243,238,0.7)',
              borderColor: 'rgba(245,243,238,0.2)',
              background: 'transparent',
            }}
          >
            ⌘K
          </span>
        </button>
      )}
      <button
        type="button"
        className="kos-nav-item"
        style={{ marginBottom: 4, width: '100%' }}
        aria-label="Search"
        onClick={() => setSearchOpen(true)}
      >
        <span className="kos-nav-icon">
          <Icon name="search" size={14} />
        </span>
        <span>Search</span>
        <span className="kos-kbd" style={{ marginLeft: 'auto' }}>
          ⌘/
        </span>
      </button>
    </>
  );

  return (
    <TweaksProvider value={{ tweaks, setTweak }}>
      <AppShell
        sidebar={
          <Sidebar
            groups={navGroupsFor(location.pathname, activeWorkoutCount)}
            activeId={activeId}
            counts={navCounts}
            alerts={navAlerts}
            disabledIds={disabledNavIds}
            brandName={currentApp.name}
            brandLogoSrc={kLogoUrl}
            brandLogoAlt=""
            footerSlot={
              <button type="button" className="kos-icon-btn" aria-label="Notifications">
                <Icon name="bell" size={13} />
              </button>
            }
            brandTrailing={<AppSwitcher currentSlug={slug} />}
            renderBrandMark={(props) =>
              // Known app → brand mark is the CURRENT app glyph, click → home.
              slug === 'tasks' || slug === 'health' || slug === 'finance' ? (
                <Link
                  to={currentApp.home}
                  className={props.className}
                  aria-label={`${currentApp.name} home`}
                  style={{ width: 28, height: 28 }}
                >
                  <AppIcon slug={slug} size={28} />
                </Link>
              ) : (
                // Platform (/apps, settings) → keep the JC monogram → /apps.
                <Link
                  to="/apps"
                  className={props.className}
                  aria-label="Open application selector"
                >
                  {props.children}
                </Link>
              )
            }
            renderBrandName={(props) => (
              <Link to={currentApp.home} className={props.className}>
                {props.children}
              </Link>
            )}
            renderItem={makeRenderItem(activeWorkoutSessions)}
            user={userMeta}
            topSlot={sidebarTop}
            renderUserCard={(u) => (
              <UserMenu
                user={{ displayName: u.name, platformRole: user.platformRole ?? null }}
                onSignOut={handleLogout}
                trigger={
                  <button type="button" className="kos-user-trigger" aria-label="Open user menu">
                    <Avatar
                      person={{ initials: u.initials, color: u.color ?? null }}
                      size={22}
                    />
                    <div className="kos-user-trigger-info">
                      <div className="kos-user-trigger-name">{u.name}</div>
                      {u.meta && <div className="kos-user-trigger-meta">{u.meta}</div>}
                    </div>
                  </button>
                }
              />
            )}
          />
        }
        drawer={previewTaskId ? <TaskPreviewDrawer taskId={previewTaskId} /> : undefined}
      >
        <Outlet />
      </AppShell>
      <QuickCapture open={qcOpen} onClose={() => setQcOpen(false)} />
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <PomodoroFinishWatcher />
    </TweaksProvider>
  );
}

/**
 * Health's "Today" nav group, with a dynamic "Active workout" item
 * unshifted at the top when at least one in-progress session exists.
 * HEALTH_NAV_GROUPS itself (packages/core) is never mutated — this
 * returns a fresh array each call.
 */
function healthNavGroupsWithActiveWorkout(activeCount: number): NavGroup[] {
  if (activeCount <= 0) return HEALTH_NAV_GROUPS;
  const activeItem: NavItem = {
    id: ACTIVE_WORKOUT_NAV_ID,
    name: 'Active workout',
    path: '/health/workouts',
    icon: 'upcoming',
  };
  return HEALTH_NAV_GROUPS.map((group) =>
    group.group === 'Today' ? { ...group, items: [activeItem, ...group.items] } : group,
  );
}

/** Pick the sidebar manifest matching the app the current path belongs to. */
function navGroupsFor(pathname: string, activeWorkoutCount = 0) {
  if (pathname.startsWith('/health')) return healthNavGroupsWithActiveWorkout(activeWorkoutCount);
  if (pathname.startsWith('/finance')) return FINANCE_NAV_GROUPS;
  return NAV_GROUPS;
}

function getActiveNavId(pathname: string): string {
  if (pathname.startsWith('/apps')) return 'apps';
  // Resolve against the right manifest depending on which app the user is in.
  // The active-workout item is transient/dynamic — it never matches the
  // pathname-based lookup below, so it's fine to resolve against the base
  // (static) manifest here; falling through to 'health-today' either way.
  const groups = navGroupsFor(pathname);
  const items = groups.flatMap((g) => g.items);
  const exact = items.find((i) => i.path === pathname || `${i.path}/` === pathname);
  if (exact) return exact.id;
  const prefix = items
    .filter((i) => pathname.startsWith(`${i.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (prefix) return prefix.id;
  if (pathname.startsWith('/health')) return 'health-today';
  if (pathname.startsWith('/finance')) return 'finance-dashboard';
  return 'today';
}

function getCurrentAppBrand(pathname: string): { name: string; home: AppHomePath } {
  if (pathname.startsWith('/health')) return { name: 'Health', home: '/health' };
  if (pathname.startsWith('/finance')) return { name: 'Finance', home: '/finance' };
  if (pathname.startsWith('/tasks')) return { name: 'Tasks', home: '/tasks' };
  return { name: 'K-OS', home: '/apps' };
}

/**
 * Slug the current app for per-app accent resolution (Aurora §2). Mirrors
 * `getCurrentAppBrand` but returns a stable lowercase key; everything outside
 * a known app (the /apps picker, settings, etc.) scopes to `'platform'`.
 */
function getCurrentAppSlug(pathname: string): string {
  if (pathname.startsWith('/health')) return 'health';
  if (pathname.startsWith('/finance')) return 'finance';
  if (pathname.startsWith('/tasks')) return 'tasks';
  return 'platform';
}
